import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  MapPin,
  NotePencil,
} from '@phosphor-icons/react';
import { DateTime } from 'luxon';
import { Badge, Button } from '@la/ds-ui-components';
import { CartItemAddOn, CartItemSubType } from '@la/types';
import { useConsolidatedCheckout } from 'lib/context/ConsolidatedCheckoutContext/ConsolidatedCheckoutContext';
import { formatDateRange } from 'lib/utils/dateUtils';
import {
  CartItemOptionsSubprogramsDetail,
  PaymentPlanSummary,
} from '../Checkout.types';
import RemoveFromCartModal from '../RemoveFromCartModal/RemoveFromCartModal';
import styles from './OrderSummaryCard.module.scss';
import OrderSummaryCardDetails from './OrderSummaryCardDetails';

type ActionButtonsProps = {
  editUrl?: string;
  isShowingAdditionalButtons?: boolean;
  showDetails: boolean;
  showDetailsHandler: () => void;
  openRemoveItemModal: () => void;
};

export type CartItemBodyProps = {
  addOns?: CartItemAddOn[];
  cartItemUuid: string;
  discountCodeBadgeLabel: string | null;
  editUrl?: string;
  isShowingAdditionalButtons?: boolean;
  location?: string;
  openPaymentPlansModal: () => void;
  paymentPlanBadgeLabel: string | null;
  programDateInfo: string | null;
  selectedPaymentPlan?: PaymentPlanSummary;
  subprograms?: CartItemOptionsSubprogramsDetail[];
  subtype: CartItemSubType;
};

type PaymentOptionsProps = {
  discountCodeBadgeLabel: string | null;
  isShowingAdditionalButtons?: boolean;
  openPaymentPlansModal: () => void;
  paymentPlanBadgeLabel: string | null;
  selectedPaymentPlanName?: string;
};

/* Order Summary Card Body*/
export default function OrderSummaryCardBody({
  addOns,
  cartItemUuid,
  discountCodeBadgeLabel,
  editUrl,
  isShowingAdditionalButtons,
  location,
  openPaymentPlansModal,
  paymentPlanBadgeLabel,
  programDateInfo,
  subprograms,
  selectedPaymentPlan,
  subtype,
}: Readonly<CartItemBodyProps>) {
  const [showDetails, setShowDetails] = useState(false);
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);
  const [isDeletingCartItem, setIsDeletingCartItem] = useState(false);
  const { handleCartItemDeletion } = useConsolidatedCheckout();

  function showDetailsHandler() {
    setShowDetails(!showDetails);
  }

  function openRemoveItemModal() {
    setShowRemoveItemModal(true);
  }

  function closeRemoveItemModal() {
    setShowRemoveItemModal(false);
  }

  function removeCartItem() {
    setIsDeletingCartItem(true);
    handleCartItemDeletion(cartItemUuid)
      .then()
      .catch((err) => {
        console.error(err);
      });
  }

  return (
    <div
      className={styles.orderSummaryCardBody}
      data-testid="order-summary-card-body"
    >
      <DivisionList location={location} programDateInfo={programDateInfo} />
      <PaymentOptions
        discountCodeBadgeLabel={discountCodeBadgeLabel}
        isShowingAdditionalButtons={isShowingAdditionalButtons}
        openPaymentPlansModal={openPaymentPlansModal}
        paymentPlanBadgeLabel={paymentPlanBadgeLabel}
        selectedPaymentPlanName={selectedPaymentPlan?.name}
      />
      <ActionButtons
        editUrl={editUrl}
        isShowingAdditionalButtons={isShowingAdditionalButtons}
        showDetails={showDetails}
        showDetailsHandler={showDetailsHandler}
        openRemoveItemModal={openRemoveItemModal}
      />
      {showDetails ? (
        <OrderSummaryCardDetails
          addOns={addOns}
          cartItemUuid={cartItemUuid}
          subprograms={subprograms}
          subtype={subtype}
        />
      ) : null}
      {showRemoveItemModal ? (
        <RemoveFromCartModal
          closeModal={closeRemoveItemModal}
          isOpen={showRemoveItemModal}
          isProcessing={isDeletingCartItem}
          mainMessage="You are now removing this item from your cart, all registration progress will be lost."
          modalTitle="Remove item"
          primaryActionLabel="Yes, remove this"
          removeCartItem={removeCartItem}
          secondaryMessage="Are you sure you want to continue?"
        />
      ) : null}
    </div>
  );
}
/* */

/* Division List*/
function DivisionList({
  location,
  programDateInfo,
}: Readonly<
  Pick<CartItemBodyProps, 'location' | 'programDateInfo' | 'subprograms'>
>) {
  return (
    <div className={styles.locationAndDate}>
      <LocationInfo location={location} />
      <ProgramDateInfo programDateInfo={programDateInfo} />
    </div>
  );
}
/* */

/* Location Info*/
function LocationInfo({ location }: { location?: string }) {
  return location ? (
    <span>
      <MapPin size={16} weight="fill" />
      {location}
    </span>
  ) : null;
}
/* */

/* Program Date Info*/
function ProgramDateInfo({
  programDateInfo,
}: Readonly<{
  programDateInfo: string | null;
}>) {
  return programDateInfo ? (
    <span>
      <CalendarBlank size={16} weight="fill" />
      {programDateInfo}
    </span>
  ) : null;
}
/* */

/* Action Buttons */
function ActionButtons({
  editUrl,
  isShowingAdditionalButtons,
  showDetails,
  showDetailsHandler,
  openRemoveItemModal,
}: Readonly<ActionButtonsProps>) {
  const navigate = useNavigate();

  function editButtonClickHandler() {
    if (editUrl) {
      navigate(editUrl);
    }
  }

  return (
    <div className={styles.actionButtons}>
      <Button
        rightIcon={
          showDetails ? (
            <CaretUp size={20} weight="bold" />
          ) : (
            <CaretDown size={20} weight="bold" />
          )
        }
        size="medium"
        variant="outline"
        onClick={showDetailsHandler}
      >
        {showDetails ? 'Hide details' : 'Show details'}
      </Button>
      {isShowingAdditionalButtons ? (
        <>
          <Button
            disabled={!editUrl}
            hasPadding={false}
            onClick={editButtonClickHandler}
            size="small"
            variant="text"
          >
            Edit
          </Button>
          <Button
            hasPadding={false}
            onClick={openRemoveItemModal}
            size="small"
            variant="text"
          >
            Remove
          </Button>
        </>
      ) : null}
    </div>
  );
}
/* */

/* Payment Options */
function PaymentOptions({
  discountCodeBadgeLabel,
  isShowingAdditionalButtons,
  openPaymentPlansModal,
  paymentPlanBadgeLabel,
  selectedPaymentPlanName,
}: Readonly<PaymentOptionsProps>) {
  return (
    <div className={styles.paymentOptions}>
      {isShowingAdditionalButtons && selectedPaymentPlanName ? (
        <PaymentPlanName
          openPaymentPlansModal={openPaymentPlansModal}
          selectedPaymentPlanName={selectedPaymentPlanName}
        />
      ) : null}
      {isShowingAdditionalButtons && paymentPlanBadgeLabel ? (
        <Badge size="small" text={paymentPlanBadgeLabel} />
      ) : null}
      {discountCodeBadgeLabel ? (
        <Badge size="small" text={discountCodeBadgeLabel} />
      ) : null}
    </div>
  );
}
/* */

/* Payment Plan Name */
function PaymentPlanName({
  openPaymentPlansModal,
  selectedPaymentPlanName,
}: Readonly<{
  openPaymentPlansModal: () => void;
  selectedPaymentPlanName: string;
}>) {
  return (
    <button onClick={openPaymentPlansModal} className={styles.paymentPlanName}>
      {selectedPaymentPlanName}
      <NotePencil weight="bold" size={20} />
    </button>
  );
}

export function formatDateForCard(startDate?: string, endDate?: string) {
  if (startDate && endDate) {
    return formatDateRange(
      DateTime.fromISO(startDate, { zone: 'utc' }),
      DateTime.fromISO(endDate, { zone: 'utc' })
    );
  } else if (startDate) {
    return formatDateRange(DateTime.fromISO(startDate, { zone: 'utc' }));
  } else {
    return null;
  }
}
